/**
 * ONE SENTENCE FOR AN UNUSABLE LEVEL MAP, AND ONE OWNER FOR IT (D6).
 *
 * This component said "level map is not available for this room." and
 * `RoomMatrix` said "the level map is not available for this room, so nothing
 * can be placed on it.", both from the same `topology.valid === false`, both on
 * screen at once about 300px apart. Two elements genuinely have to speak,
 * because they are in two different places (the spine is in the shell's command
 * band, the grid is in Coach's panel), but they must not compose the sentence
 * twice: the matrix appends its own consequence to this clause and nothing
 * rewords it.
 *
 * IT LIVES HERE, IN THE SHELL, AND NOT IN `coachCopy`, and the direction is the
 * whole reason. The containment gate's check A3 forbids a shell module
 * importing a page: the shell is a parent of every surface, so one such import
 * pulls that page's module graph into all of them, and its allowlist is a
 * record of existing debt that new edges may not join. Page to shell is the
 * permitted direction, so the sentence sits with the element that says it first
 * and the matrix reaches UP for it.
 */
export const LEVEL_MAP_UNAVAILABLE =
  "the level map is not available for this room";

/**
 * THE LEVEL SPINE. How many squads are standing on each rung, and which rung is
 * the teacher's lens.
 *
 * IT READS THE LOCATION READING, NOT THE PACE MAP (FE-9.9b). The nodes read
 * `classification.placement.byLevel`. They used to read `classification.byLevel`,
 * which is the PACE map: `classifyRoom`'s terminal branch returns before
 * `byLevel` is ever written, on purpose, because work that is in has stopped
 * being compared to a lens. That is right for a pace reading and wrong for this
 * one, whose whole promise is the sentence above: how many squads are STANDING
 * on each rung. A fully turned-in room rendered every node as a zero while the
 * panel counted eleven squads turned in, and read as a glitch because as a
 * location reading it was one.
 *
 * `byLevel` IS REPLACED HERE, NEVER REPURPOSED, which is what the comment block
 * in `classifyRoom` demands of anyone who reaches this problem. It keeps its
 * meaning, keeps its byte-pin in `RoomMatrix.test.jsx`, and now has no UI
 * consumer at all; retiring it is its own change with its own review.
 *
 * NO SPECIAL STYLING FOR A RUNG HOLDING ONLY TURNED-IN SQUADS. A count is a
 * count. The spine states location; the stage a squad is at belongs to the
 * matrix and to the work state bar, and a third grammar for it here would be
 * the duplication D3 was about.
 *
 * IT NO LONGER CARRIES TWO TALLIES (D3, F-C5). It rendered "N not evidenced"
 * and "N work turned in" under the spine, which the room reading renders again
 * about 200px below: the work state bar states turned in, and since FE-9.9a the
 * pace sentence states not evidenced. The band keeps the spine and gives up the
 * tallies, and the accessible caption drops them for the same reason. The one
 * number the caption does add is a different claim from either: squads that
 * cannot be PLACED on a rung at all, in words that are neither of the two the
 * band may not restate.
 */
export const RoomPulse = ({ classification, expectedLevel }) => {
  const capsules = classification?.topology?.capsules || [];
  const placement = classification?.placement?.byLevel;
  const unplaceable = classification?.placement?.unplaceable || [];
  const onRung = (levelNumber) => placement?.get(levelNumber)?.length || 0;

  if (!classification?.topology?.valid) {
    return (
      /* THE SAME DEFECT AS THE BAND'S LOADING BOX, AND THE SAME FIX (S-1, P1).
         One sentence inside `rounded-xl border bg-background-cloudburst/30` on
         a surface that is already a card. The sentence is unchanged and stays
         exactly where it was; only its box goes. */
      <div className="min-w-0">
        <p className="text-sm text-lv2">{`${LEVEL_MAP_UNAVAILABLE}.`}</p>
      </div>
    );
  }

  return (
    <figure className="min-w-0" aria-labelledby="room-pulse-caption">
      <figcaption id="room-pulse-caption" className="sr-only">
        room pulse. expected pace is level {expectedLevel || "not recorded"}.
        {capsules.map(
          (capsule) =>
            ` level ${capsule.levelNumber}: ${onRung(capsule.levelNumber)} squads.`,
        )}
        {/* THE ONE CLAUSE THE SPINE OWES AN ASSISTIVE READER, and the reason
            the words are these words. A squad whose level identity is presumed
            or unavailable, or whose capsule disagrees with the topology, is on
            no rung, so the nodes cannot account for it and the sum of the
            caption would silently be short. The matrix's detached strip is its
            visible home and the spine adds no node for it. "not placed on a
            level" is deliberately neither "not evidenced" nor "turned in": the
            band may not restate either of those tallies (F-C5), and this is a
            different claim from both anyway. */}
        {unplaceable.length > 0
          ? ` ${unplaceable.length} squads are not placed on a level.`
          : ""}
      </figcaption>

      <div
        className="relative max-w-full overflow-x-auto px-1 pb-1 pt-1"
        aria-hidden="true"
      >
        <div className="absolute left-5 right-5 top-6 h-px bg-gradient-to-r from-transparent via-charlotte/25 to-transparent" />
        <ol
          className="relative grid list-none gap-2"
          style={{
            gridTemplateColumns: `repeat(${capsules.length}, minmax(32px, 1fr))`,
          }}
        >
          {capsules.map((capsule) => {
            const count = onRung(capsule.levelNumber);
            const expected = Number(expectedLevel) === capsule.levelNumber;
            return (
              <li
                key={capsule.id}
                className="flex min-w-0 flex-col items-center"
              >
                <span
                  className={[
                    "relative flex h-8 w-8 items-center justify-center rounded-full border text-sm font-semibold transition-all duration-normal sm:h-11 sm:w-11 sm:text-md",
                    expected
                      ? "border-charlotte bg-charlotte/15 text-charlotte shadow-glow-charlotte"
                      : count > 0
                        ? "border-white/15 bg-background-biscay text-lv1"
                        : "border-white/[0.07] bg-background-midnight text-lv4",
                  ].join(" ")}
                >
                  {count}
                  {expected ? (
                    <span className="absolute -bottom-1 h-1.5 w-1.5 rounded-full bg-charlotte" />
                  ) : null}
                </span>
                <span
                  className={
                    expected
                      ? "mt-2 text-xs text-charlotte"
                      : "mt-2 text-xs text-lv4"
                  }
                >
                  level {capsule.levelNumber}
                </span>
              </li>
            );
          })}
        </ol>
      </div>
    </figure>
  );
};

export default RoomPulse;
